import { motion, useScroll, useTransform, MotionValue } from "framer-motion";
import { useRef } from "react";
import { 
  AlertTriangle, 
  CheckCircle2, 
  FileX, 
  FileCheck, 
  Users, 
  UserCheck,
  XCircle,
  Shield
} from "lucide-react";
import { useTranslation } from "react-i18next";

const icons = {
  AlertTriangle,
  CheckCircle2,
  FileX,
  FileCheck,
  Users,
  UserCheck,
  XCircle,
  Shield
};

export function TransformationSection() {
  const { t } = useTranslation('home');
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const progress = useTransform(scrollYProgress, [0.2, 0.8], [0, 1]);
  
  const transformationItems = t('transformationSection.items', { returnObjects: true }) as Array<{
    before: { icon: string; text: string };
    after: { icon: string; text: string };
  }>;

  const transformations = transformationItems.map(item => ({
    before: { icon: icons[item.before.icon as keyof typeof icons], text: item.before.text, color: "text-red-400" },
    after: { icon: icons[item.after.icon as keyof typeof icons], text: item.after.text, color: "text-emerald-400" },
  }));

  return (
    <section 
      ref={containerRef}
      className="py-12 md:py-16 lg:py-20 bg-gradient-to-b from-background to-secondary/20 overflow-hidden"
    >
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-block text-accent font-bold text-sm uppercase tracking-widest mb-4 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
            {t('transformationSection.badge')}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {t('transformationSection.title')}
            <span className="text-gradient-accent"> {t('transformationSection.titleAccent')}</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl">
            {t('transformationSection.subtitle')}
          </p>
        </motion.div>

        {/* Transformation Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {transformations.map((item, index) => (
            <motion.div
              key={index}
              className="relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <TransformationCard 
                before={item.before}
                after={item.after}
                progress={progress}
                index={index}
                beforeLabel={t('transformationSection.beforeLabel')}
                afterLabel={t('transformationSection.afterLabel')}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface TransformationCardProps {
  before: { icon: React.ComponentType<{ className?: string }>; text: string; color: string };
  after: { icon: React.ComponentType<{ className?: string }>; text: string; color: string };
  progress: MotionValue<number>;
  index: number;
  beforeLabel: string;
  afterLabel: string;
}

function TransformationCard({ before, after, progress, index, beforeLabel, afterLabel }: TransformationCardProps) {
  const BeforeIcon = before.icon;
  const AfterIcon = after.icon;
  
  const delay = index * 0.1;
  const cardProgress = useTransform(progress, [delay, delay + 0.3], [0, 1]);
  const beforeOpacity = useTransform(cardProgress, (v) => v < 0.5 ? 1 - v * 2 : 0);
  const afterOpacity = useTransform(cardProgress, (v) => v > 0.5 ? (v - 0.5) * 2 : 0);
  const beforeX = useTransform(cardProgress, (v) => v < 0.5 ? -v * 60 : -30);
  const afterX = useTransform(cardProgress, (v) => v > 0.5 ? 30 - (v - 0.5) * 60 : 30);
  const progressWidth = useTransform(cardProgress, (v) => `${v * 100}%`);

  return (
    <div className="relative h-[100px] sm:h-[120px] rounded-xl sm:rounded-2xl border border-border bg-card overflow-hidden">
      {/* Progress Bar */}
      <motion.div 
        className="absolute top-0 left-0 h-1 bg-gradient-to-r from-red-400 via-accent to-emerald-400"
        style={{ width: progressWidth }}
      />
      
      {/* Before State */}
      <motion.div 
        className="absolute inset-0 flex items-center gap-3 sm:gap-4 p-4 sm:p-6"
        style={{ opacity: beforeOpacity, x: beforeX }}
      >
        <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
          <BeforeIcon className={`w-5 h-5 sm:w-7 sm:h-7 ${before.color}`} />
        </div>
        <div>
          <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 sm:mb-1">{beforeLabel}</p>
          <p className="font-bold text-foreground text-sm sm:text-lg">{before.text}</p>
        </div>
      </motion.div>
      
      {/* After State */}
      <motion.div 
        className="absolute inset-0 flex items-center gap-3 sm:gap-4 p-4 sm:p-6"
        style={{ opacity: afterOpacity, x: afterX }}
      >
        <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
          <AfterIcon className={`w-5 h-5 sm:w-7 sm:h-7 ${after.color}`} />
        </div>
        <div>
          <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 sm:mb-1">{afterLabel}</p>
          <p className="font-bold text-foreground text-sm sm:text-lg">{after.text}</p>
        </div>
      </motion.div>
    </div>
  );
}
